import { z } from 'zod';
import { connectedAppScopeSchema } from './connected-app';
import { ulidSchema } from './id';

/**
 * First-party integrations — B73, `BE-73.1` / `FR-AUTO-029`.
 *
 * This is a deterministic catalogue and safe account-state contract. It does
 * not model provider OAuth credentials or arbitrary provider payloads.
 */

export const FIRST_PARTY_INTEGRATION_KEYS = [
  'slack',
  'jira',
  'confluence',
  'google_drive',
  'microsoft_teams',
  'gmail',
  'outlook',
] as const;

export const FIRST_PARTY_INTEGRATION_STATES = [
  'not_configured',
  'connected',
  'revoked',
  'no_access',
  'unavailable',
] as const;

export const FIRST_PARTY_INTEGRATION_STATE_REASONS = [
  'board_not_enabled',
  'account_connected',
  'account_revoked',
  'viewer_lacks_provider_access',
  'power_up_missing',
] as const;

export const firstPartyIntegrationKeySchema = z.enum(FIRST_PARTY_INTEGRATION_KEYS);
export type FirstPartyIntegrationKey = z.infer<typeof firstPartyIntegrationKeySchema>;

export const firstPartyIntegrationStateSchema = z.enum(FIRST_PARTY_INTEGRATION_STATES);
export type FirstPartyIntegrationState = z.infer<typeof firstPartyIntegrationStateSchema>;

export const firstPartyIntegrationStateReasonSchema = z.enum(
  FIRST_PARTY_INTEGRATION_STATE_REASONS,
);
export type FirstPartyIntegrationStateReason = z.infer<
  typeof firstPartyIntegrationStateReasonSchema
>;

const timestampStringSchema = z.string().min(1).max(64);

export const firstPartyConnectedAccountSchema = z.strictObject({
  connectedAppId: ulidSchema,
  scope: connectedAppScopeSchema,
  grantedAt: timestampStringSchema,
  expiresAt: timestampStringSchema.nullable(),
  lastActiveAt: timestampStringSchema.nullable(),
  revokedAt: timestampStringSchema.nullable(),
});

export type FirstPartyConnectedAccount = z.infer<typeof firstPartyConnectedAccountSchema>;

export const firstPartyIntegrationSchema = z.strictObject({
  key: firstPartyIntegrationKeySchema,
  name: z.string().min(1).max(64),
  description: z.string().min(1).max(255),
  powerUpId: ulidSchema.nullable(),
  boardPowerUpId: ulidSchema.nullable().optional(),
  enabledOnBoard: z.boolean().optional(),
  state: firstPartyIntegrationStateSchema,
  stateReason: firstPartyIntegrationStateReasonSchema.nullable(),
  connectedAccount: firstPartyConnectedAccountSchema.nullable(),
});

export type FirstPartyIntegration = z.infer<typeof firstPartyIntegrationSchema>;

export const firstPartyIntegrationListSchema = z.strictObject({
  boardId: ulidSchema.nullable().optional(),
  integrations: z
    .array(firstPartyIntegrationSchema)
    .length(FIRST_PARTY_INTEGRATION_KEYS.length),
});

export type FirstPartyIntegrationList = z.infer<typeof firstPartyIntegrationListSchema>;
